import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Zap, 
  Calendar, 
  Users, 
  LogOut, 
  Settings, 
  MapPin,
  Menu,
  X,
  AlertTriangle,
  BookOpen,
  Home,
  History,
  User
} from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../contexts/SettingsContext";
import SettingsDialog from "../components/SettingsDialog";
import LakeAustinInfo from "../components/LakeAustinInfo";
import EmergencyContacts from "../components/EmergencyContacts";
import CalendarScheduler from "../components/CalendarScheduler";
import TeamMemberDashboard from "../components/TeamMemberDashboard";
import TeamMemberProfile from "../components/TeamMemberProfile";
import TeamMemberHistory from "../components/TeamMemberHistory";
import { BACKEND_URL } from "../config/api";

// BACKEND_URL imported from config/api
const API = `${BACKEND_URL}/api`;

const TeamMemberViewV2 = () => {
  const { dateFormat, timeFormat } = useSettings();
  const [teamMemberData, setTeamMemberData] = useState(null);
  const [activeView, setActiveView] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false); // Collapsed by default
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("teamMemberData");
    if (!stored) {
      navigate("/");
      return;
    }
    setTeamMemberData(JSON.parse(stored));
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("teamMemberData");
    localStorage.removeItem("userId");
    localStorage.removeItem("userType");
    localStorage.removeItem("userData");
    navigate("/");
    toast.success("Logged out successfully");
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'history', label: 'History', icon: History },
    { id: 'lake', label: 'Water Info', icon: MapPin },
    { id: 'emergency', label: 'Emergency', icon: AlertTriangle },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } fixed lg:relative transition-all duration-300 bg-white border-r border-gray-200 flex flex-col h-screen z-50 overflow-hidden`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-6 h-6 rounded-lg object-cover"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{color: '#1a1a1a'}}>
                Charter <span style={{color: '#007AFF'}}>Share</span>
              </h1>
              <p className="text-xs text-gray-500">TeamMember View</p>
            </div>
          </div>
        </div>

        {/* TeamMember Info */}
        <div className="p-4 border-b bg-gray-50">
          <p className="text-sm text-gray-600">Logged in as</p>
          <p className="font-semibold text-gray-800">{teamMemberData?.name}</p>
          {teamMemberData?.doubleDeckerCertified && (
            <Badge className="mt-2 bg-blue-500 text-white text-xs">⭐ DD Certified</Badge>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant={activeView === item.id ? "default" : "ghost"}
                className="w-full justify-start"
                onClick={() => setActiveView(item.id)}
                style={activeView === item.id ? { background: '#007AFF' } : {}}
                data-nav={item.id}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </Button>
            ))}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings className="w-5 h-5 mr-3" />
            Settings
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          
          <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
            {navItems.find(item => item.id === activeView)?.label || 'TeamMember Dashboard'}
          </h1>
          
          <div className="w-10" /> {/* Spacer for mobile */}
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-32">
          {activeView === 'dashboard' && team_memberData && (
            <TeamMemberDashboard 
              teamMemberId={teamMemberData.id}
              team_memberData={teamMemberData}
            />
          )}

          {activeView === 'schedule' && team_memberData && (
            <CalendarScheduler 
              readOnly={true}
              teamMemberId={teamMemberData.id}
              showMyBookingsToggle={true}
            />
          )}

          {activeView === 'profile' && team_memberData && (
            <TeamMemberProfile 
              teamMemberId={teamMemberData.id}
              team_memberData={teamMemberData}
              onUpdate={(updatedData) => setTeamMemberData(updatedData)}
            />
          )}

          {activeView === 'history' && team_memberData && (
            <TeamMemberHistory 
              teamMemberId={teamMemberData.id}
              team_memberData={teamMemberData}
            />
          )}
          
          {activeView === 'lake' && <LakeAustinInfo />}
          {activeView === 'emergency' && <EmergencyContacts />}
        </div>
      </div>

      {/* Settings Dialog */}
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
};

export default TeamMemberViewV2;
